import { memo, useEffect, useRef, useState } from 'react';
import { CircleMarker, Tooltip, useMap } from 'react-leaflet';
import { api } from '../lib/api';
import type { Filters } from '../lib/filters';
import type { PropertyFeature } from '../lib/types';
import { oppColor, oppTier } from '../lib/opportunity';
import { BBOX_RETRY_MS, readBbox } from '../lib/mapBbox';
import { flyOptions } from '../lib/motion';

interface Props {
  filters: Filters;
  selectedId: string | null;
  /** Propiedad bajo el cursor (card de la lista o marker) — se resalta. */
  hoveredId?: string | null;
  onSelect: (id: string | null) => void;
  /** Notifica hover de markers para resaltar la card correspondiente. */
  onHover?: (id: string | null) => void;
  /** Notifica al padre el estado del fetch para mostrar overlay/pill. */
  onStatusChange?: (s: { loading: boolean; visible: number; total?: number | null }) => void;
}

/** Cap duro del endpoint geojson — mismo valor que el clamp del backend. */
export const GEOJSON_LIMIT = 500;

const BBOX_DEBOUNCE_MS = 400;

/**
 * Carga las properties del bbox actual + filtros y las renderiza como
 * CircleMarkers. Re-fetcha al panear/zoom (con debounce) Y cada vez
 * que `filters` cambia. Click → onSelect(id) → App abre el drawer.
 *
 * Tooltip on hover con preview compacto; el detalle completo se ve
 * en el drawer (PropertyDetail).
 */
export function PropertyMarkers({ filters, selectedId, hoveredId, onSelect, onHover, onStatusChange }: Props) {
  const [features, setFeatures] = useState<PropertyFeature[]>([]);
  const map = useMap();
  const refetchRef = useRef<() => void>(() => {});

  // Primera carga sin debounce (mismo motivo que en PublicMarkers: ese
  // timer inicial se puede cancelar antes de disparar y dejar el mapa
  // vacío hasta que el usuario lo mueva).
  const firstLoadRef = useRef(true);

  useEffect(() => {
    let timer: number | undefined;
    // Un AbortController POR REQUEST, no uno compartido por todo el ciclo
    // del efecto: con el compartido, las respuestas nunca se cancelaban
    // individualmente y un pan-A lento que aterrizaba después de un pan-B
    // rápido pintaba markers del bbox viejo. Ahora cada refetch aborta al
    // anterior en vuelo.
    let inflight: AbortController | null = null;

    const run = () => {
        const bbox = readBbox(map);
        if (!bbox) {
          // Contenedor sin alto todavía: un bbox degenerado devolvería 0.
          map.invalidateSize();
          timer = window.setTimeout(run, BBOX_RETRY_MS);
          return;
        }
        inflight?.abort();
        const ctrl = new AbortController();
        inflight = ctrl;
        onStatusChange?.({ loading: true, visible: 0 });
        api
          .propertiesGeoJson({ limit: GEOJSON_LIMIT, bbox, filters }, ctrl.signal)
          .then((fc) => {
            // Solo la request MÁS RECIENTE puede escribir estado.
            if (inflight !== ctrl) return;
            setFeatures(fc.features);
            // total (RFC-0021 A5): coincidencias globales con estos filtros,
            // para que MapView avise el truncamiento del cap de 500.
            onStatusChange?.({ loading: false, visible: fc.features.length, total: fc.total ?? null });
          })
          .catch((err: unknown) => {
            if (inflight !== ctrl) return; // abortada por un refetch más nuevo
            if ((err as { name?: string } | null)?.name === 'AbortError') return;
            onStatusChange?.({ loading: false, visible: 0 });
          });
    };

    refetchRef.current = () => {
      window.clearTimeout(timer);
      const delay = firstLoadRef.current ? 0 : BBOX_DEBOUNCE_MS;
      firstLoadRef.current = false;
      timer = window.setTimeout(run, delay);
    };

    refetchRef.current();
    const handler = () => refetchRef.current();
    map.on('moveend', handler);
    map.on('zoomend', handler);
    return () => {
      inflight?.abort();
      window.clearTimeout(timer);
      map.off('moveend', handler);
      map.off('zoomend', handler);
    };
  }, [map, filters, onStatusChange]);

  // Al seleccionar una propiedad (click en marker o en la lista), centramos el
  // mapa SOLO si el punto está fuera de la vista actual — así no movemos el
  // mapa de forma molesta cuando ya es visible. Cubre los markers cargados en
  // el bbox; los items de la lista sin coords o fuera del set no disparan fly.
  useEffect(() => {
    if (!selectedId) return;
    const f = features.find((x) => x.id === selectedId);
    if (!f || !f.geometry) return;
    const [lng, lat] = f.geometry.coordinates;
    if (!map.getBounds().contains([lat, lng])) {
      map.flyTo([lat, lng], Math.max(map.getZoom(), 13), flyOptions(0.6));
    }
  }, [selectedId, features, map]);

  return (
    <>
      {features.map((f) => (
        <PropertyMarkerItem
          key={f.id}
          feature={f}
          isSelected={f.id === selectedId}
          isHovered={f.id === hoveredId && f.id !== selectedId}
          onSelect={onSelect}
          onHover={onHover}
        />
      ))}
    </>
  );
}

/**
 * Marker individual memoizado: `hoveredId` vive en App, así que cada
 * movimiento de mouse entre cards/markers re-renderizaba los 500
 * CircleMarkers con sus Tooltips. Con memo, solo los 1-2 markers cuyo
 * estado hovered/selected cambió se reconcilian. onSelect/onHover son
 * setters de App (identidad estable) — el memo es efectivo.
 */
const PropertyMarkerItem = memo(function PropertyMarkerItem({
  feature: f,
  isSelected,
  isHovered,
  onSelect,
  onHover,
}: {
  feature: PropertyFeature;
  isSelected: boolean;
  isHovered: boolean;
  onSelect: (id: string | null) => void;
  onHover?: (id: string | null) => void;
}) {
  if (!f.geometry) return null;
  const [lng, lat] = f.geometry.coordinates;
  const p = f.properties;
  const isRisk = p.zonaRiesgoFlag === true;
  const radius = isSelected ? 12 : isHovered ? 11 : priceToRadius(p.price);
  // Color por tier de opportunity score (verde→gris); slate si sin score.
  const fill = oppColor(p.opportunityScore);
  // Borde: blanco si seleccionado u hovered (desde lista o mapa),
  // naranja si zona de riesgo, si no el mismo tono del relleno (sutil).
  const stroke = isSelected || isHovered ? '#fff' : isRisk ? '#f97316' : fill;
  const weight = isSelected ? 2.8 : isHovered ? 2.4 : isRisk ? 2.2 : 1.4;
  return (
    <CircleMarker
      center={[lat, lng]}
      radius={radius}
      pathOptions={{
        color: stroke,
        fillColor: fill,
        fillOpacity: isSelected || isHovered ? 0.95 : 0.78,
        weight,
      }}
      eventHandlers={{
        click: () => onSelect(f.id),
        mouseover: () => onHover?.(f.id),
        mouseout: () => onHover?.(null),
      }}
    >
      <Tooltip direction="top" offset={[0, -4]} opacity={0.97}>
        <MarkerTooltip p={p} />
      </Tooltip>
    </CircleMarker>
  );
});

/**
 * Tooltip enriquecido del marker: mismo nivel de analytics que las cards de
 * la lista (score, descuento vs avalúo, $/m², CENAPRED) en vez del preview
 * pobre anterior. El detalle completo sigue en el drawer.
 */
function MarkerTooltip({ p }: { p: PropertyFeature['properties'] }) {
  const tier = p.opportunityScore !== null ? oppTier(p.opportunityScore) : null;
  return (
    <div className="popup-card">
      <div className="price">
        {p.price !== null && p.price > 1 ? formatMoney(p.price) : 'sin precio'}
        {p.discountPct !== null && p.discountPct > 0 && (
          <span className="discount">-{p.discountPct.toFixed(0)}%</span>
        )}
        {p.opportunityScore !== null && tier && (
          <span className={`popup-opp opp-${tier}`}>{Math.round(p.opportunityScore)}</span>
        )}
      </div>
      <div className="addr">{p.address}</div>
      <div className="meta">
        {p.propertyType} · {p.channel} · {p.municipio ?? '—'}
      </div>
      {(p.discountVsAvaluoPct !== null || p.priceM2 !== null) && (
        <div className="popup-stats">
          {p.discountVsAvaluoPct !== null && p.discountVsAvaluoPct >= 1 && (
            <span>−{p.discountVsAvaluoPct.toFixed(0)}% vs avalúo</span>
          )}
          {p.priceM2 !== null && p.priceM2 > 0 && (
            <span>{formatMoney(p.priceM2)}/m²</span>
          )}
        </div>
      )}
      {(p.cenapredSeismicZone || p.cenapredFloodRisk || p.zonaRiesgoFlag) && (
        <div className="popup-flags">
          {p.zonaRiesgoFlag && <span className="popup-flag risk">⚠ Zona riesgo</span>}
          {p.cenapredSeismicZone && (
            <span className="popup-flag">Sísmica {p.cenapredSeismicZone}</span>
          )}
          {p.cenapredFloodRisk && (
            <span className="popup-flag">Inund. {p.cenapredFloodRisk}</span>
          )}
        </div>
      )}
    </div>
  );
}

function priceToRadius(price: number | null): number {
  if (price === null || price <= 1) return 5;
  // log scale: $100k → 4, $1M → 7, $10M → 10.
  const r = 2 + Math.log10(price / 10_000);
  return Math.max(4, Math.min(12, r));
}

function formatMoney(n: number): string {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    maximumFractionDigits: 0,
  }).format(n);
}
